import { Box, Table, TableBody, TableCell, TableRow, IconButton, LinearProgress } from "@mui/material";
import { DeliveryTimeIcon } from "components/DeliveryTimeIcon";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { IPunctualityOrder } from "interfaces/IPunctuality";
import { formatLateTime } from "../../utils/formatters";
import { getDeliveryStatus, getRowStatus } from "../../utils/status";
import { StyledTableContainer, StyledTableHead, StyledTableRow, SortableColumn, SortIconButton } from "./styles";
import { formatDate, formatTime } from "utils/dateFormat";

type SortField = 'dt' | 'delay' | 'delivery_date' | 'city';

interface TableProps {
  orders: IPunctualityOrder[];
  currentOrderBy?: SortField;
  currentOrderDirection?: 'asc' | 'desc';
  onSort: (orderBy: SortField, orderDirection: 'asc' | 'desc') => void;
  showLoading?: boolean;
}

const SortIcon = ({
  backendField,
  currentOrderBy,
  currentOrderDirection
}: {
  backendField: SortField;
  currentOrderBy?: SortField;
  currentOrderDirection?: 'asc' | 'desc'
}) => {
  if (currentOrderBy !== backendField) {
    return <ArrowUpwardIcon sx={{ color: 'rgba(0, 0, 0, 0.54)', fontSize: '16px' }} />;
  }
  return currentOrderDirection === 'asc' ?
    <ArrowUpwardIcon sx={{ color: 'rgba(0, 0, 0, 0.54)', fontSize: '16px' }} />
    : <ArrowDownwardIcon sx={{ color: 'rgba(0, 0, 0, 0.54)', fontSize: '16px' }} />;
};

export const PunctualityTable = ({
  orders,
  currentOrderBy,
  currentOrderDirection,
  onSort,
  showLoading = false
}: TableProps) => {

  // Função para lidar com cliques de ordenação
  const handleSort = (backendField: SortField) => {
    const newDirection =
      currentOrderBy === backendField && currentOrderDirection === 'asc'
        ? 'desc'
        : 'asc';

    onSort(backendField, newDirection);
  };

  return (
    <StyledTableContainer>
      <Table>
        <StyledTableHead>
          <TableRow>
            <TableCell sx={{ userSelect: 'none' }}>ID do pedido</TableCell>
            <TableCell sx={{ userSelect: 'none' }}>Cliente</TableCell>
            <TableCell sx={{ userSelect: 'none' }}>Bairro</TableCell>
            <TableCell sx={{ userSelect: 'none' }}>Motorista</TableCell>
            <TableCell>
              <SortableColumn onClick={() => handleSort('city')}>
                Cidade
                <SortIconButton size="small" className="sort-icon" active={currentOrderBy === 'city'}>
                  <SortIcon
                    backendField="city"
                    currentOrderBy={currentOrderBy}
                    currentOrderDirection={currentOrderDirection}
                  />
                </SortIconButton>
              </SortableColumn>
            </TableCell>
            <TableCell>
              <SortableColumn onClick={() => handleSort('delivery_date')}>
                Data de entrega
                <SortIconButton size="small" className="sort-icon" active={currentOrderBy === 'delivery_date'}>
                  <SortIcon
                    backendField="delivery_date"
                    currentOrderBy={currentOrderBy}
                    currentOrderDirection={currentOrderDirection}
                  />
                </SortIconButton>
              </SortableColumn>
            </TableCell>
            <TableCell sx={{ userSelect: 'none' }}>Prazo</TableCell>
            <TableCell sx={{ userSelect: 'none' }}>Horário de entrega</TableCell>
            <TableCell>
              <SortableColumn onClick={() => handleSort('delay')}>
                Atraso
                <SortIconButton size="small" className="sort-icon" active={currentOrderBy === 'delay'}>
                  <SortIcon
                    backendField="delay"
                    currentOrderBy={currentOrderBy}
                    currentOrderDirection={currentOrderDirection}
                  />
                </SortIconButton>
              </SortableColumn>
            </TableCell>
          </TableRow>

          {/* Barra de loading fixa junto com o cabeçalho */}
          {showLoading && (
            <TableRow>
              <TableCell colSpan={9} sx={{
                padding: 0,
                border: 'none',
                position: 'sticky',
                top: '48px',
                zIndex: 10,
                backgroundColor: 'white'
              }}>
                <LinearProgress />
              </TableCell>
            </TableRow>
          )}
        </StyledTableHead>

        <TableBody sx={{
          opacity: showLoading ? 0.6 : 1,
          transition: 'opacity 0.2s ease'
        }}>
          {orders.map((row: IPunctualityOrder, index: number) => (
            <StyledTableRow key={index} status={getRowStatus(row.lateTime)}>
              <TableCell>{row.orderId}</TableCell>
              <TableCell>{row.cnpjInt}</TableCell>
              <TableCell>{row.bairro}</TableCell>
              <TableCell>{row.driverName}</TableCell>
              <TableCell>{row.city}</TableCell>
              <TableCell>{formatDate(row.attemptTime)}</TableCell>
              <TableCell>{formatTime(row.scheduledTo)}</TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  {formatTime(row.attemptTime)}
                  {' '}
                  <DeliveryTimeIcon timing={getDeliveryStatus(row.lateTime)} />
                </Box>
              </TableCell>
              <TableCell>{formatLateTime(row.lateTime)}</TableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};
